<template>
  <pu-map
    ref="puMap"
    v-bind="puMapProps"
    :seen-campsites="seenCampsitesStore.seenCampsites"
    :initial-zoom="initialZoom"
    :bounds-to-fit="boundsToFit"
    :show-layer-selector="true"
    class="pu-map"
    @boundsChange="$emit('boundsChange', $event)"
    @dragstart="$emit('dragstart')"
    @marker-clicked="onMarkerClicked"
    @marker-selected="onMarkerSelected"
  >
    <template #dynamicPopup>
      <div class="pu-infowindow">
        <slot name="dynamicPopup" />
      </div>
    </template>
  </pu-map>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { getWKTPoint } from '../../../../utility/geo/latLng'
import { logger } from '../../../../utility/logger'
import { SearchFilters } from '../../../maps-search/schemas'
import { PuMapProps } from '../pu-map/PuMapProps'
import { Marker, MarkerType } from '../pu-map/types'
import CampsitesMapBase from './CampsitesMapBase'
import { ResultCampsitesMapProps, ResultMapCampsite } from './CampsitesMapProps'

@Component({
  mixins: [CampsitesMapBase],
  emits: ['boundsChange', 'dragstart', 'marker-selected'],
  setup() {
    const seenCampsitesStore = useSeenCampsitesStore(usePinia())
    return { seenCampsitesStore }
  },
})
class CampsiteResultsMap extends Vue implements ResultCampsitesMapProps {
  @Prop({ required: true })
  campsites: ResultMapCampsite[]

  @Prop({ required: true })
  searchFilters: SearchFilters

  @Prop()
  boundsToFit?: any

  seenCampsitesStore: ReturnType<typeof useSeenCampsitesStore>

  get initialZoom() {
    return 6
  }

  get computedCentre() {
    logger('CampsiteResultsMap.centre', this.searchFilters.area)
    return this.searchFilters?.area?.lat && this.searchFilters?.area?.lng
      ? getWKTPoint(this.searchFilters.area.lat, this.searchFilters.area.lng)
      : this.centre
  }

  get puMapProps(): PuMapProps {
    return {
      markers: this.markers,
      initialBounds: this.initialBounds,
      circle: this.circle,
      visibleMarker: this.visibleMarker,
      centre: this.computedCentre,
      useClustering: this.useClustering,
      showGeolocationIcon: true,
      zoomControl: !!this.$isDesktop,
      scrollWheelZoom: true,
    }
  }

  // TODO: If we manage to check isFavourite in MapsMain then we can avoid this loop and pass it from the parent
  // considering looping over 1k+ objects this can be a good optimisation?
  get markers(): Marker[] {
    return this.campsites.map(({ slug, name, point, price, id }) => {
      return {
        campsiteId: id,
        name,
        position: point,
        price,
        slug,
        type: price ? MarkerType.price : MarkerType.icon,
      }
    })
  }

  onMarkerSelected(slug: string) {
    this.$emit('marker-selected', slug)
  }

  onMarkerClicked(marker) {
    this.$emit('marker-clicked', marker)
    this.seenCampsitesStore.add(marker.slug)
  }

  getBounds() {
    const puMap = this.$refs.puMap
    if (puMap) {
      return puMap.getBounds()
    }
  }
}

export default toNative(CampsiteResultsMap)
</script>
<style scoped>
.pu-map {
  width: 100%;
  height: 100%;
}
</style>
